<template>
	<template v-if="data">
		<TheForm :model="data" :rules="rules" ref="formRef" :waiting="waiting" :id="formId">
			<ButtonSticky @buttonclick="saveUser()" isSubmit :disabled="isDisabled">
				<template v-slot:icon><saveIcon /></template>
				<template v-slot:text> Speichern </template>
			</ButtonSticky>

			<section>
				<ButtonBack />

				<el-row class="flex flex--alignstart">
					<el-col :sm="12" :xs="24">
						<h1>{{ localTitle }}</h1>
					</el-col>
					<el-col :sm="12" :xs="24" class="flex flex--justifyend">
						<div :class="`has-status has-status--${$getStateColor('user', data.status)}`">
							{{ $getStateFullLabel('user', data.status, false) }}
						</div>
					</el-col>
				</el-row>

				<el-row class="button-panel">
					<TheButton type="danger" @buttonclick="openEditAbsenceModal = true" :disabled="data.status == UserStatus.Deactivated">
						{{ data.lastUserAbsence ? 'Abwesenheit bearbeiten' : 'Abwesenheit erstellen' }}
					</TheButton>
					<TheButton
						type="danger"
						@buttonclick="openDialogHandleUserActivation = true"
						:disabled="data.status === UserStatus.Absent"
						v-if="data.status !== UserStatus.Pending"
					>
						{{ data.status === UserStatus.Active ? 'User deaktivieren' : 'User reaktivieren' }}
					</TheButton>
				</el-row>

				<TheBox :title="'User Login'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="E-Mail-Adresse:" prop="email">
								<el-input v-model="data.email" :placeholder="'Bitte E-Mail eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Erstellt am:" prop="createdDate">
								<span>{{ data.createdDate ? $formatDate(data.createdDate) : '-' }}</span>
							</el-form-item>
						</el-col>

						<el-col :sm="12" :xs="24">
							<el-form-item label="Letzter Login:" prop="lastLogin">
								<span>{{ data.lastLogin ? $formatDate(data.lastLogin) : '-' }}</span>
							</el-form-item>

							<el-form-item label="ID:" prop="hid">
								<span>{{ data.hid }}</span>
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>

				<TheBox :title="'User Daten'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Anrede:" class="datagrid--input-short" prop="gender">
								<el-select v-model="data.gender" :placeholder="'Bitte wählen'" :disabled="isDisabled">
									<el-option v-for="option in genderOptions" :key="option.value" :label="option.label" :value="option.value" />
								</el-select>
							</el-form-item>

							<el-form-item label="Titel (vor):" prop="titleBefore">
								<el-input v-model="data.titleBefore" :placeholder="'Bitte Titel eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Titel (nach):" prop="titleAfter">
								<el-input v-model="data.titleAfter" :placeholder="'Bitte Titel eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Vorname:" prop="firstName">
								<el-input v-model="data.firstName" :placeholder="'Bitte Vorname eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Nachname:" prop="lastName">
								<el-input v-model="data.lastName" :placeholder="'Bitte Nachname eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Initialen:" class="datagrid--input-short" prop="initials">
								<el-input v-model="data.initials" :placeholder="'Bitte Initialen eingeben'" :disabled="isDisabled" />
							</el-form-item>
						</el-col>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Pro-Ethik-Siegel:" prop="seal">
								<TheToggle :active="data.seal" @toggle="data.seal = !data.seal" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Firma" prop="company">
								<el-input v-model="data.company" :placeholder="'Bitte Firma eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Telefon" prop="phoneNumber">
								<el-input v-model="data.phoneNumber" :placeholder="'Bitte Telefonnummer eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Straße:" prop="street">
								<el-input v-model="data.street" :placeholder="'Bitte Straße eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="PLZ:" prop="postalCode">
								<el-input v-model="data.postalCode" :placeholder="'Bitte PLZ eingeben'" :disabled="isDisabled" />
							</el-form-item>

							<el-form-item label="Ort:" prop="city">
								<el-input v-model="data.city" :placeholder="'Bitte Ort eingeben'" :disabled="isDisabled" />
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>
			</section>

			<section class="no-marginbottom">
				<el-row>
					<el-col :sm="12" :xs="24">
						<h2>Gruppenzugehörigkeit</h2>
					</el-col>
					<el-col :sm="12" :xs="24" class="flex flex--justifyend">
						<TheButton @buttonclick="openDialogNewRole = true" v-show="!isDisabled">
							<el-icon><Plus /></el-icon>Neue Gruppenzugehörigkeit
						</TheButton>
					</el-col>
				</el-row>
			</section>
			<RoleSlider :roles="data.roles ? data.roles : []" @delete="(role) => removeRoleFromUser(role)" :showDelete="!isDisabled" />

			<section>
				<TheBox :title="'Interne Notiz (nur für Mitarbeiter sichtbar)'">
					<el-input v-model="data.internalNote" :autosize="{ minRows: 3 }" type="textarea" placeholder="Interne Notiz" :disabled="isDisabled" />
				</TheBox>

				<TheBox :id="'log'" :title="'Log'">
					<LogsTable :logs="logs" :loading="logsLoading" />
				</TheBox>
			</section>
		</TheForm>

		<DialogHandleUserActivation v-model:open="openDialogHandleUserActivation" :user="data" @reload="loadUser" />
		<DialogNewRole v-model:open="openDialogNewRole" :userId="id" @reload="loadUser" :roleOptions="availableRoles" />
		<DialogEditUserAbsence v-model:open="openEditAbsenceModal" :userId="id" :currentAbsence="data.lastUserAbsence" @reload="loadUser" />
	</template>
	<section v-else-if="loading === false">
		<ButtonBack />
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import { Delete, Plus, Loading } from '@element-plus/icons-vue';
import { defineComponent, ref, markRaw } from 'vue';
import saveIcon from '../assets/icons/saveIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import RoleSlider from '../components/slider/RoleSlider.vue';

import { validateForm } from '../utils/functions';

import { AuditResponse, SaveUserRequest, UserResponse, UserStatus } from '../types/types';
import { texts } from '../utils/texts';
import { categories } from '@/utils/categories';
import { addRoleToUser, getUser, getUserLogs, removeRoleFromUser, saveUser } from '@/requests/users';
import { returnFormOptions } from '@/utils/formOptions';
import BoxRole from '@/components/boxes/BoxRole.vue';
import TheToggle from '@/components/TheToggle.vue';
import DialogHandleUserActivation from '@/components/dialogs/DialogHandleUserActivation.vue';
import DialogNewRole from '@/components/dialogs/DialogNewRole.vue';
import DialogEditUserAbsence from '@/components/dialogs/DialogEditUserAbsence.vue';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import LogsTable from '@/components/tables/LogsTable.vue';
import { ElMessage, FormInstance } from 'element-plus';

const ElMessageDuration = 3000;

export default defineComponent({
	name: 'UserDetailView',
	data() {
		return {
			data: null as UserResponse | null,
			formId: 'userForm',
			rules: {
				gender: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur', 'change'] }],
				firstName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				lastName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				email: [
					{
						required: true,
						message: texts.formErrors.emailReq,
						trigger: 'blur',
					},
					{
						type: 'email',
						message: texts.formErrors.emailMissing,
						trigger: ['blur'],
					},
				],
				initials: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' },
					{ min: 2, message: texts.formErrors.minChars, trigger: 'blur' },
					{ max: 3, message: texts.formErrors.maxChars, trigger: 'blur' },
				],
			},
			waiting: false,
			loading: true,
			categories,
			openEditAbsenceModal: false,
			openDialogNewRole: false,
			openDialogHandleUserActivation: false,
			UserStatus,
			logs: [] as AuditResponse[],
			logsLoading: true,
			localTitle: '',
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		TheBox,
		Plus,
		Loading,
		Delete,
		saveIcon,
		BoxRole,
		TheToggle,
		DialogHandleUserActivation,
		DialogNewRole,
		DialogEditUserAbsence,
		LogsTable,
		RoleSlider,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		genderOptions() {
			return returnFormOptions('gender');
		},
		availableRoles() {
			if (!this.data) return [];
			return returnFormOptions('userRole').filter((role) => !this.data!.roles.some((userRole) => userRole.name === role.value?.name));
		},
		isDisabled() {
			return (this.data && this.data.status === UserStatus.Deactivated) || false;
		},
	},
	methods: {
		async saveUser() {
			if (!this.data) return;

			const isValid = await validateForm(this.formRef, this.formId);
			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const saveUserRequest: SaveUserRequest = {
				userId: this.data.id,
				email: this.data.email,
				firstName: this.data.firstName,
				lastName: this.data.lastName,
				seal: this.data.seal,
				gender: this.data.gender,
				postalCode: this.data.postalCode,
				city: this.data.city,
				street: this.data.street,
				phoneNumber: this.data.phoneNumber,
				company: this.data.company,
				titleBefore: this.data.titleBefore,
				titleAfter: this.data.titleAfter,
				initials: this.data.initials,
				internalNote: this.data.internalNote,
			};

			const result = await saveUser(saveUserRequest);
			if (result.success) {
				this.loadUser();
				this.localTitle = this.data.firstName + ' ' + result.data.lastName;
				ElMessage({ message: this.texts.user.saveUserSuccess, type: 'success', duration: ElMessageDuration });
			} else {
				ElMessage({ message: this.texts.user.saveUserFailed, type: 'error', duration: ElMessageDuration });
			}

			this.waiting = false;
			waitingMsg.close();
		},
		async loadUser() {
			const loading = startLoading('#user');

			const result = await getUser(this.id);
			if (result.success) {
				this.data = result.data;
				this.localTitle = this.data.firstName + ' ' + result.data.lastName;
				this.loadLogs();
			}

			this.loading = false;
			stopLoading(loading);
		},
		async removeRoleFromUser(role) {
			const loading = startLoading('#user');

			const removeResult = await removeRoleFromUser(this.id, role.name);
			if (removeResult.success) {
				await this.loadUser();
			} else {
				ElMessage({ message: texts.user.removeRoleFailed, type: 'error', duration: 1500 });
			}

			stopLoading(loading);
		},

		async loadLogs() {
			this.logsLoading = true;
			const result = await getUserLogs(this.id);
			if (result.success) {
				this.logs = result.data;
			}
			this.logsLoading = false;
		},
	},
	mounted() {
		this.loadUser();
	},
});
</script>
